import { Descriptions } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { CgCloseR } from "react-icons/cg";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import useNotification from "../../../hooks/useNotification";
import { INotice } from "../../../models/homepageManagementHomeNotice.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import FileUpload from "../../FileUpload";
import Radio from "../../Radio";
import SelectCampus from "../../SelectCampus";
import Tinymce from "../../Tinymce";

export interface IProps {
  data?: INotice;
  submit: (data: FormData) => void;
}

const NoticeEditor: FC<IProps> = ({
  data,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { infoNotification } = useNotification();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);

  const [title, setTitle] = useState('');
  const [bulletinSeq, setBulletinSeq] = useState('2');
  const [openYn, setOpenYn] = useState('Y');
  const [content, setContent] = useState('');
  const [file, setFile] = useState<any>();
  const [files, setFiles] = useState<any>([null]);
  const [isCreate, setIsCreate] = useState(true);
  const [thumbnailFile, setThumbnailFile] = useState<any>();
  const [noticeFile, setNoticeFile] = useState<any>([]);
  const [readCount, setReadCount] = useState(0);

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setBulletinSeq(String(data.bulletinSeq));
      setOpenYn(data.openYn);
      setContent(data.content);
      setThumbnailFile(data.thumbnailFile);
      setNoticeFile(data.noticeFile);
      setReadCount(data.readCount);
      setIsCreate(false);
    }
  }, [data]);

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  }

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  }

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  }

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.archive.message1'));
    }
    if (!content) {
      return infoNotification(t('homepage.archive.message4'));
    }
    if (isCreate && !file) {
      return infoNotification(t('homepage.education.message4'));
    }

    const form = new FormData();
    form.append('title', title);
    form.append('campusCode', currentCampus);
    form.append('openYn', openYn);
    form.append('bulletinSeq', bulletinSeq);
    form.append('content', content);
    if (file) {
      if (isCreate) {
        form.append('thumbnailFile', file);
      } else {
        form.append('thumbnailFileStatus', 'update');
        form.append('thumbnailNewFile', file);
      }
    } else if (!isCreate) {
      form.append('thumbnailFileStatus', 'keep');
    }

    if (isCreate) {
      /* 생성 시 파일 처리 */
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('noticeFile', item);
        });
      }
    } else {
      /* 수정 시 파일 처리 */
      if (data?.thumbnailFileCode) {
        form.append('thumbnailFileCode', String(data.thumbnailFileCode));
      }

      if (data?.noticeFileCode) {
        form.append('noticeFileCode', String(data.noticeFileCode));
      }

      if (noticeFile) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let isAllDeleted = true;
        let hasDeleted = false;
        noticeFile.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('noticeDeleteFiles', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('noticeFileStatus', 'update');
        else if (isAllDeleted) form.append('noticeFileStatus', 'delete');
        else if (hasDeleted) form.append('noticeFileStatus', 'update');
        else form.append('noticeFileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('noticeFileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('noticeFileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('noticeNewFile', item);
        });
      }
    }

    submit(form);
  }
  
  const onDeleteAttachedFile = (index: number) => {
    const _noticeFile = [...noticeFile];
    _noticeFile[index].originalName = null;
    setNoticeFile(_noticeFile);
  }

  return (
    <div className='comm-table__area rowType'>
      <Descriptions
        bordered
        column={3}
        size="small"
      >
        <Descriptions.Item label={`* ${t('common.column.title')}`} span={3}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('common.column.writer')}>
          {auth?.korName}
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column8')}>
          {moment(serverDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column7')}>
          {readCount}
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column11')}>
          <div className="w-150p">
            <SelectCampus />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.notice.column1')}>
          <Radio
            checked={openYn === 'Y'}
            onChange={() => setOpenYn('Y')}
            text={t('common.option.yes')}
          />
          <Radio
            className="ml-4"
            checked={openYn === 'N'}
            onChange={() => setOpenYn('N')}
            text={t('common.option.no')}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.notice.column2')}>
          <Radio
            checked={bulletinSeq === '1'}
            onChange={() => setBulletinSeq('1')}
            text={t('common.option.yes')}
          />
          <Radio
            className="ml-4"
            checked={bulletinSeq === '2'}
            onChange={() => setBulletinSeq('2')}
            text={t('common.option.no')}
          />          
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('common.column.contents')}`} span={3}>
          <Tinymce
            content={content}
            setContent={setContent}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.education.column5')}`} span={3}>
          {
            thumbnailFile
              ? (
                <div>
                  <a
                    className="download-link mr-1"
                    href={thumbnailFile.fileLocation}
                    download={thumbnailFile.originalName}
                  >
                    {thumbnailFile.originalName}
                  </a>
                </div>
                )
              : null
          }
          <FileUpload
            file={file}
            setFile={setFile}
          />
        </Descriptions.Item>
        {
          noticeFile?.length
            ? (
              <Descriptions.Item label={t('common.column.existFile')} span={3}>
                {
                  noticeFile.map((item: any, index: number) => {
                    return item?.originalName
                      ? (
                        <div>
                          <a
                            className="download-link"
                            href={`${item.fileLocation}`}
                            download={item.originalName}
                          >
                            {item.originalName}
                          </a>
                          <span
                            className="ml-1 pointer"
                            onClick={() => onDeleteAttachedFile(index)}
                          >
                            <CgCloseR />
                          </span>
                        </div>
                      )
                      : null;
                  })
                }                
              </Descriptions.Item>
              )
            : null
        }
        <Descriptions.Item label={isCreate ? t('common.column.file') : t('common.column.additFile')} span={3}>
          {
            files.length > 0
              ? files.map((file: any, index: number) => (
                <div className="flex mb-2" key={index}>
                  <FileUpload
                    file={files[index]}
                    setFile={(file: any) => onSetFile(index, file)}
                  />
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small gray ml-2"
                      onClick={() => onDeleteFile(index)}
                      text={t('common.button.delete')}
                    />
                  </div>
                </div>
              ))
              : null
          }
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink-line"
              onClick={onAddFile}
              text={t('common.button.add')}
            />
          </div>
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSubmit}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/homepage/notice${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </div>
  );
}

export default NoticeEditor;
